import { ProfileData, ProfileField } from "../types/profile";
import { getCountriesWithFlags, type CountryWithFlag } from "../../../config/country.config";
import { TOTAL_FACTORY_SIZE_OPTIONS } from "../../../constants/factorySizeOptions";

interface ProfileApiResponse {
  id: string;
  email: string;
  auth0Id?: string;
  userId?: string;
  auth0Email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  whatsapp?: string;
  bio?: string;
  website?: string;
  linkedinProfile?: string;
  profileImage?: string;
  businessType?: string;
  country?: string;
  role: string;
  isActive?: boolean;
  sellerStatus?: string;
  createdAt?: Date;
  updatedAt?: Date;
  editableFields?: string[];
  nonEditableFields?: string[];
  company?: {
    id: string;
    name: string;
    email?: string;
    phone?: string;
    address?: string;
    city?: string;
    state?: string;
    country?: string;
    postalCode?: string;
    website?: string;
    description?: string;
    businessType?: string;
    companySize?: string;
    employeeCount?: number;
    industry?: string;
    foundedYear?: number;
    gstNumber?: string;
    panNumber?: string;
    gstDocumentUrl?: string;
    panDocumentUrl?: string;
    taxId?: string;
    registrationNumber?: string;
    businessLicense?: string;
    factorySize?: string;
    productionCapacity?: string;
    qualityCertifications?: string;
    facebookUrl?: string;
    linkedinUrl?: string;
    twitterUrl?: string;
    logo?: string;
    approvalStatus?: string;
    approvedAt?: Date;
    isActive?: boolean;
    // Seller Business Profile (pepagora-style)
    legalBusinessName?: string;
    businessName?: string;
    legalStatus?: string;
    documentType?: string;
    documentNumber?: string;
    annualTurnover?: string;
    shippingAddressSameAsBusiness?: boolean;
    mainMarkets?: string[];
    shipmentModes?: string[];
    acceptedPaymentCurrency?: string | string[];
    paymentTerms?: string | string[];
    paymentMethods?: string[];
    iecNumber?: string;
    iecDocumentUrl?: string;
    exportPercentage?: number;
    languagesSpoken?: string[];
    nearestPort?: string;
    averageLoadTimeValue?: string;
    averageLoadTimeUnit?: string;
    minOrderValueCurrency?: string;
    minOrderValueAmount?: string;
    acceptedDeliveryTerms?: string;
    contractManufacturing?: string;
    totalFactorySize?: string;
    infrastructureOverview?: string;
    productionLinesCount?: number;
    annualOutputValue?: string;
    qcStaffCount?: number;
    rndStaffCount?: number;
    productionFacilities?: string;
    warehouseStorageArea?: string;
    companyVideoUrl?: string;
    youtubeUrl?: string;
    instagramUrl?: string;
    businessProfileExtended?: Record<string, unknown>;
    selectedMembershipPlanId?: string;
    industryTypes?: string[];
    sellerAddresses?: {
      address?: string;
      city?: string;
      state?: string;
      country?: string;
      postalCode?: string;
    }[];
  };
}

function toStringArray(value: string | string[] | undefined | null): string[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value.map((v) => String(v).trim()).filter(Boolean);
  }
  const s = String(value).trim();
  return s ? [s] : [];
}

/**
 * Maps API response to ProfileData structure
 */
export const mapApiResponseToProfileData = (
  apiData: ProfileApiResponse,
  registrationData?: {
    email?: string;
    phone?: string;
    firstName?: string;
    lastName?: string;
  },
): ProfileData => {
  const editableFields = apiData.editableFields || [];
  const nonEditableFields = apiData.nonEditableFields || [];

  // Determine if email should be editable (non-editable from registration)
  const isEmailEditable = !nonEditableFields.includes("email");
  // Phone can be edited even if from registration
  const isPhoneEditable = editableFields.includes("phone");
  const isWhatsappEditable = editableFields.includes("whatsapp");
  const isWebsiteEditable = editableFields.includes("website");

  // Use registration data to prefill, but use API data as source of truth
  const email = registrationData?.email || apiData.email || "";
  const phone = apiData.phone || registrationData?.phone || "";
  const firstName = apiData.firstName || registrationData?.firstName || "";
  const lastName = apiData.lastName || registrationData?.lastName || "";

  const profileData: ProfileData = {
    userInfo: {
      profilePic: {
        label: "Profile Picture",
        value: apiData.profileImage || "",
        editable: editableFields.includes("profileImage"),
        type: "upload",
        required: false,
      },
      firstName: {
        label: "First Name",
        value: firstName,
        editable: editableFields.includes("firstName"),
        type: "text",
        required: true,
      },
      lastName: {
        label: "Last Name",
        value: lastName,
        editable: editableFields.includes("lastName"),
        type: "text",
        required: true,
      },
      email: {
        label: "Email Address",
        value: email,
        editable: isEmailEditable,
        type: isEmailEditable ? "text" : "readonly",
        required: true,
      },
      phone: {
        label: "Phone Number",
        value: phone,
        editable: isPhoneEditable,
        type: "text",
        required: false,
        placeholder: "Shared with sellers when you send an enquiry",
      },
      whatsapp: {
        label: "WhatsApp Number",
        value: apiData.whatsapp || "",
        editable: isWhatsappEditable,
        type: "text",
        required: false,
        placeholder: "Leave blank if same as phone or not applicable",
      },
      website: {
        label: "Website",
        value: apiData.website || "",
        editable: isWebsiteEditable,
        type: "text",
        required: false,
        placeholder: "https://yourwebsite.com",
      },
      userId: {
        label: "User ID",
        value: apiData.id,
        editable: false,
        type: "readonly",
      },
      role: {
        label: "Role",
        value: apiData.role ? String(apiData.role).toUpperCase() : "",
        editable: false, // Will be enabled by ensureEmailDisabled when userId is provided
        type: "readonly",
        options: [
          { label: "Buyer", value: "BUYER" },
          { label: "Seller", value: "SELLER" },
          { label: "Buyer & Seller", value: "BUYER_SELLER" },
          { label: "Company Admin", value: "COMPANY_ADMIN" },
          // Super Admin option is excluded - only super admins can have this role and it cannot be changed
          { label: "Employee", value: "EMPLOYEE" },
        ],
      },
      bio: {
        label: "Bio",
        value: apiData.bio || "",
        editable: editableFields.includes("bio"),
        type: "text",
        required: false,
        placeholder: "Tell us about yourself...",
      },
      businessType: {
        label: "Business Type",
        value: apiData.businessType || "",
        editable: editableFields.includes("businessType"),
        type: "text",
        required: false,
      },
      country: {
        label: "Country",
        value: apiData.country || "",
        editable: editableFields.includes("country"),
        type: "select",
        required: false,
        options: getCountriesWithFlags().map((c: CountryWithFlag) => ({
          label: `${c.flag} ${c.name}`,
          value: c.name,
        })),
      },
      sellerStatus: {
        label: "Seller Status",
        value: apiData.sellerStatus || "",
        editable: false,
        type: "readonly",
        required: false,
      },
      isActive: {
        label: "Account Status",
        value: apiData.isActive ? "Active" : "Inactive",
        editable: false, // Will be enabled by ensureEmailDisabled when userId is provided
        type: "readonly",
        required: false,
      },
      createdAt: {
        label: "Created At",
        value: apiData.createdAt
          ? new Date(apiData.createdAt).toLocaleDateString()
          : "",
        editable: false,
        type: "readonly",
        required: false,
      },
      updatedAt: {
        label: "Last Updated",
        value: apiData.updatedAt
          ? new Date(apiData.updatedAt).toLocaleDateString()
          : "",
        editable: false,
        type: "readonly",
        required: false,
      },
      auth0Id: {
        label: "Auth0 ID",
        value: apiData.auth0Id || "",
        editable: false,
        type: "readonly",
        required: false,
      },
    },
    companyInfo: {},
    socialLinks: {
      linkedin: {
        label: "LinkedIn Profile",
        value: apiData.linkedinProfile || "",
        editable: editableFields.includes("linkedinProfile"),
        type: "text",
        placeholder: "https://linkedin.com/in/yourprofile",
      },
      website: {
        label: "Personal Website",
        value: apiData.website || "",
        editable: editableFields.includes("website"),
        type: "text",
        placeholder: "https://yourwebsite.com",
      },
    },
    contactPerson: {},
    subAccountManagement: {},
    products: {},
  };

  // Add company information if available (for sellers)
  if (apiData.company) {
    profileData.companyInfo = {
      companyName: {
        label: "Company Name",
        value: apiData.company.name || "",
        editable: editableFields.includes("companyName"),
        type: "text",
        required: true,
      },
      companyEmail: {
        label: "Company Email",
        value: apiData.company.email || "",
        editable: false,
        type: "text",
        required: true,
      },
      companyPhone: {
        label: "Company Phone",
        value: apiData.company.phone || "",
        editable: editableFields.includes("companyPhone"),
        type: "text",
        required: false,
      },
      address: {
        label: "Company Address",
        value: apiData.company.address || "",
        editable: editableFields.includes("companyAddress"),
        type: "text",
        required: false,
        placeholder: "At least one of address, city or country required",
      },
      city: {
        label: "City",
        value: apiData.company.city || "",
        editable: editableFields.includes("companyCity"),
        type: "text",
        required: false,
      },
      state: {
        label: "State/Province",
        value: apiData.company.state || "",
        editable: editableFields.includes("companyState"),
        type: "text",
        required: false,
      },
      country: {
        label: "Company Country",
        value: apiData.company.country || apiData.country || "",
        editable: editableFields.includes("companyCountry"),
        type: "text",
        required: false,
      },
      postalCode: {
        label: "Postal Code",
        value: apiData.company.postalCode || "",
        editable: editableFields.includes("companyPostalCode"),
        type: "text",
        required: false,
      },
      companyWebsite: {
        label: "Company Website",
        value: apiData.company.website || "",
        editable: editableFields.includes("companyWebsite"),
        type: "text",
        placeholder: "https://companywebsite.com",
      },
      description: {
        label: "About your company",
        value: apiData.company.description || "",
        editable: editableFields.includes("companyDescription"),
        type: "text",
        required: false,
        placeholder:
          "Describe your business, products, capabilities, and values. This appears on your public storefront profile.",
      },
      companyBusinessType: {
        label: "Business Type",
        value: apiData.company.businessType || "",
        editable: editableFields.includes("companyBusinessType"),
        type: "text",
        required: false,
      },
      companySize: {
        label: "Company Size",
        value: apiData.company.companySize || "",
        editable: editableFields.includes("companySize"),
        type: "text",
        required: false,
      },
      employeeCount: {
        label: "Employee Count",
        value: apiData.company.employeeCount?.toString() || "",
        editable: editableFields.includes("employeeCount"),
        type: "number",
        required: false,
      },
      industry: {
        label: "Industry",
        value: apiData.company.industry || "",
        editable: editableFields.includes("industry"),
        type: "text",
        required: false,
      },
      foundedYear: {
        label: "Founded Year",
        value: apiData.company.foundedYear?.toString() || "",
        editable: editableFields.includes("foundedYear"),
        type: "number",
        required: false,
      },
      gstNumber: {
        label: "GST Number",
        value: apiData.company.gstNumber || "",
        editable: editableFields.includes("gstNumber"),
        type: "text",
        required: false,
      },
      panNumber: {
        label: "PAN Number",
        value: apiData.company.panNumber || "",
        editable: editableFields.includes("panNumber"),
        type: "text",
        required: false,
      },
      gstDocumentUrl: {
        label: "GST Document",
        value: apiData.company.gstDocumentUrl || "",
        editable: editableFields.includes("gstDocumentUrl"),
        type: "upload",
        required: false,
      },
      panDocumentUrl: {
        label: "PAN Document",
        value: apiData.company.panDocumentUrl || "",
        editable: editableFields.includes("panDocumentUrl"),
        type: "upload",
        required: false,
      },
      taxId: {
        label: "Tax ID",
        value: apiData.company.taxId || "",
        editable: editableFields.includes("taxId"),
        type: "text",
        required: false,
      },
      registrationNumber: {
        label: "Registration Number",
        value: apiData.company.registrationNumber || "",
        editable: editableFields.includes("registrationNumber"),
        type: "text",
        required: false,
      },
      businessLicense: {
        label: "Business License",
        value: apiData.company.businessLicense || "",
        editable: editableFields.includes("businessLicense"),
        type: "text",
        required: false,
      },
      factorySize: {
        label: "Factory Size",
        value: apiData.company.factorySize || "",
        editable: editableFields.includes("factorySize"),
        type: "text",
        required: false,
      },
      productionCapacity: {
        label: "Production Capacity",
        value: apiData.company.productionCapacity || "",
        editable: editableFields.includes("productionCapacity"),
        type: "text",
        required: false,
      },
      qualityCertifications: {
        label: "Quality Certifications",
        value: apiData.company.qualityCertifications || "",
        editable: editableFields.includes("qualityCertifications"),
        type: "text",
        required: false,
      },
      facebookUrl: {
        label: "Facebook URL (Optional)",
        value: apiData.company.facebookUrl || "",
        editable: editableFields.includes("facebookUrl"),
        type: "text",
        required: false,
        placeholder: "https://facebook.com/company",
      },
      linkedinUrl: {
        label: "LinkedIn URL (Optional)",
        value: apiData.company.linkedinUrl || "",
        editable: editableFields.includes("linkedinUrl"),
        type: "text",
        required: false,
        placeholder: "https://linkedin.com/company/company",
      },
      twitterUrl: {
        label: "Twitter URL (Optional)",
        value: apiData.company.twitterUrl || "",
        editable: editableFields.includes("twitterUrl"),
        type: "text",
        required: false,
        placeholder: "https://twitter.com/company",
      },
      companyLogo: {
        label: "Company Logo",
        value: apiData.company.logo || "",
        editable: editableFields.includes("companyLogo"),
        type: "upload",
        required: false,
      },
      approvalStatus: {
        label: "Approval Status",
        value: apiData.company.approvalStatus || "",
        editable: false,
        type: "readonly",
        required: false,
      },
      approvedAt: {
        label: "Approved At",
        value: apiData.company.approvedAt
          ? new Date(apiData.company.approvedAt).toLocaleDateString()
          : "",
        editable: false,
        type: "readonly",
        required: false,
      },
      // Contact / Business Profile aliases
      companyAddress: {
        label: "Business Address",
        value: apiData.company.address || "",
        editable: editableFields.includes("companyAddress"),
        type: "text",
        required: false,
        placeholder: "Add address line",
      },
      companyCountry: {
        label: "Country",
        value: apiData.company.country || "",
        editable: editableFields.includes("companyCountry"),
        type: "select",
        required: false,
        placeholder: "Country",
        options: getCountriesWithFlags().map((c: CountryWithFlag) => ({
          label: `${c.flag} ${c.name}`,
          value: c.name,
        })),
      },
      companyState: {
        label: "State",
        value: apiData.company.state || "",
        editable: editableFields.includes("companyState"),
        type: "text",
        required: false,
        placeholder: "Enter State",
      },
      companyCity: {
        label: "City",
        value: apiData.company.city || "",
        editable: editableFields.includes("companyCity"),
        type: "text",
        required: false,
        placeholder: "Enter City",
      },
      companyPostalCode: {
        label: "Pincode",
        value: apiData.company.postalCode || "",
        editable: editableFields.includes("companyPostalCode"),
        type: "text",
        required: false,
        placeholder: "Pincode",

        
      },
      shippingAddressSameAsBusiness: {
        label: "My Shipping address is same as my business address",
        value: !!apiData.company.shippingAddressSameAsBusiness,
        editable: editableFields.includes("shippingAddressSameAsBusiness"),
        type: "switch",
        required: false,
      },
      legalBusinessName: {
        label: "Legal Business Name",
        value: apiData.company.legalBusinessName || apiData.company.name || "",
        editable: editableFields.includes("legalBusinessName"),
        type: "text",
        required: true,
        placeholder: "Enter Legal Business Name",
      },
      businessName: {
        label: "Business Name",
        value: apiData.company.businessName || apiData.company.name || "",
        editable: editableFields.includes("businessName"),
        type: "text",
        required: false,
        placeholder: "Enter Business Name",
      },
      selectedMembershipPlanId: {
        label: "Membership plan",
        value: apiData.company.selectedMembershipPlanId || "",
        editable: editableFields.includes("selectedMembershipPlanId"),
        type: "text",
        required: false,
        placeholder: "Select plan",
      },
      industryTypes: {
        label: "Industry types",
        value: Array.isArray(apiData.company.industryTypes)
          ? apiData.company.industryTypes
          : [],
        editable: editableFields.includes("industryTypes"),
        type: "checkboxGroup",
        required: false,
        placeholder: "Select one or more industries",
      },
      legalOwnerName: {
        label: "Legal Owner Name",
        value: [firstName, lastName].filter(Boolean).join(" ") || "",
        editable: false,
        type: "text",
        required: false,
        readOnlyHint:
          "This field is not editable. It shows the account holder name from your profile (first and last name).",
      },
      legalStatus: {
        label: "Legal Status",
        value: apiData.company.legalStatus || "",
        editable: editableFields.includes("legalStatus"),
        type: "radio",
        required: false,
        options: [
          { label: "Unregistered Business", value: "unregistered" },
          { label: "Registered Business", value: "registered" },
          { label: "Nonprofit Organization", value: "nonprofit" },
        ],
      },
      documentType: {
        label: "Document Type",
        value: apiData.company.documentType || "",
        editable: editableFields.includes("documentType"),
        type: "select",
        required: false,
        placeholder: "Select Document type",
        options: [
          { label: "GST Certificate", value: "gst" },
          { label: "Trade License", value: "trade_license" },
          { label: "Incorporation Certificate", value: "incorporation" },
          { label: "Other", value: "other" },
        ],
      },
      documentNumber: {
        label: "Document Number",
        value: apiData.company.documentNumber || "",
        editable: editableFields.includes("documentNumber"),
        type: "text",
        required: false,
        placeholder: "Enter Document Id",
      },
      annualTurnover: {
        label: "Annual Turnover",
        value: apiData.company.annualTurnover || "",
        editable: editableFields.includes("annualTurnover"),
        type: "select",
        required: false,
        placeholder: "Select Annual Turnover",
        options: [
          { label: "Less than $100K", value: "0-100k" },
          { label: "$100K - $500K", value: "100k-500k" },
          { label: "$500K - $1M", value: "500k-1m" },
          { label: "$1M - $10M", value: "1m-10m" },
          { label: "$10M - $50M", value: "10m-50m" },
          { label: "Above $50M", value: "50m+" },
        ],
      },
      shippingAddressLine: {
        label: "Shipping Address",
        value: apiData.company.sellerAddresses?.[0]?.address || "",
        editable: true,
        type: "text",
        required: false,
        placeholder: "Add address line",
      },
      mainMarkets: {
        label: "Main Markets",
        value: Array.isArray(apiData.company.mainMarkets)
          ? apiData.company.mainMarkets
          : [],
        editable: editableFields.includes("mainMarkets"),
        type: "checkboxGroup",
        required: false,
        placeholder: "Select Main Markets",
        options: [
          { label: "North America", value: "north_america" },
          { label: "Europe", value: "europe" },
          { label: "Asia Pacific", value: "asia_pacific" },
          { label: "Middle East", value: "middle_east" },
          { label: "South America", value: "south_america" },
          { label: "Africa", value: "africa" },
          { label: "Domestic", value: "domestic" },
        ],
      },
      shipmentModes: {
        label: "Shipment Modes",
        value: Array.isArray(apiData.company.shipmentModes)
          ? apiData.company.shipmentModes
          : [],
        editable: editableFields.includes("shipmentModes"),
        type: "checkboxGroup",
        required: false,
        options: [
          { label: "Sea", value: "sea" },
          { label: "Air", value: "air" },
          { label: "Road", value: "road" },
          { label: "Rail", value: "rail" },
          { label: "Courier", value: "courier" },
          { label: "Buyer-Arranged", value: "buyer_arranged" },
          { label: "Local Delivery", value: "local_delivery" },
        ],
      },
      acceptedPaymentCurrency: {
        label: "Accepted Payment Currency",
        value: toStringArray(apiData.company.acceptedPaymentCurrency),
        editable: editableFields.includes("acceptedPaymentCurrency"),
        type: "checkboxGroup",
        required: false,
        placeholder: "Select Accepted Currency",
        options: [
          { label: "USD", value: "USD" },
          { label: "EUR", value: "EUR" },
          { label: "INR", value: "INR" },
          { label: "GBP", value: "GBP" },
          { label: "Other", value: "other" },
        ],
      },
      paymentMethods: {
        label: "Payment Methods",
        value: Array.isArray(apiData.company.paymentMethods)
          ? apiData.company.paymentMethods
          : [],
        editable: editableFields.includes("paymentMethods"),
        type: "checkboxGroup",
        required: false,
        options: [
          { label: "Credit Card", value: "credit_card" },
          { label: "Cash", value: "cash" },
          { label: "Cheque", value: "cheque" },
          { label: "Demand Draft", value: "demand_draft" },
          { label: "Paypal", value: "paypal" },
          { label: "MoneyGram", value: "moneygram" },
          { label: "Western Union", value: "western_union" },
          { label: "Others", value: "others" },
        ],
      },
      paymentTerms: {
        label: "Payment Terms",
        value: toStringArray(apiData.company.paymentTerms),
        editable: editableFields.includes("paymentTerms"),
        type: "checkboxGroup",
        required: false,
        placeholder: "Select Payment Terms",
        options: [
          { label: "Advance", value: "advance" },
          { label: "COD", value: "cod" },
          { label: "LC", value: "lc" },
        ],
      },
      iecNumber: {
        label: "IEC Number",
        value: apiData.company.iecNumber || "",
        editable: editableFields.includes("iecNumber"),
        type: "text",
        required: false,
        placeholder: "Enter IEC Number",
      },
      iecDocumentUrl: {
        label: "IEC Document",
        value: apiData.company.iecDocumentUrl || "",
        editable: editableFields.includes("iecDocumentUrl"),
        type: "upload",
        required: false,
      },
      exportPercentage: {
        label: "Export Percentage",
        value: apiData.company.exportPercentage?.toString() ?? "0",
        editable: editableFields.includes("exportPercentage"),
        type: "number",
        required: false,
      },
      languagesSpoken: {
        label: "Languages Spoken",
        value: Array.isArray(apiData.company.languagesSpoken)
          ? apiData.company.languagesSpoken
          : [],
        editable: editableFields.includes("languagesSpoken"),
        type: "checkboxGroup",
        required: false,
        placeholder: "Select Languages Spoken",
        options: [
          { label: "English", value: "english" },
          { label: "Hindi", value: "hindi" },
          { label: "Spanish", value: "spanish" },
          { label: "French", value: "french" },
          { label: "German", value: "german" },
          { label: "Chinese", value: "chinese" },
          { label: "Arabic", value: "arabic" },
        ],
      },
      nearestPort: {
        label: "Nearest Port",
        value: apiData.company.nearestPort || "",
        editable: editableFields.includes("nearestPort"),
        type: "text",
        required: false,
        placeholder: "Enter Nearest Port",
      },
      averageLoadTimeValue: {
        label: "Average Load Time",
        value: apiData.company.averageLoadTimeValue || "",
        editable: editableFields.includes("averageLoadTimeValue"),
        type: "text",
        required: false,
        placeholder: "Enter Value",
      },
      averageLoadTimeUnit: {
        label: "Duration",
        value: apiData.company.averageLoadTimeUnit || "",
        editable: editableFields.includes("averageLoadTimeUnit"),
        type: "select",
        required: false,
        placeholder: "Select Duration",
        options: [
          { label: "Days", value: "days" },
          { label: "Weeks", value: "weeks" },
          { label: "Months", value: "months" },
        ],
      },
      minOrderValueCurrency: {
        label: "Minimum Order Value (Currency)",
        value: apiData.company.minOrderValueCurrency || "",
        editable: editableFields.includes("minOrderValueCurrency"),
        type: "select",
        required: false,
        placeholder: "Select Currency",
        options: [
          { label: "USD", value: "USD" },
          { label: "EUR", value: "EUR" },
          { label: "INR", value: "INR" },
        ],
      },
      minOrderValueAmount: {
        label: "Minimum Order Value (Amount)",
        value: apiData.company.minOrderValueAmount || "",
        editable: editableFields.includes("minOrderValueAmount"),
        type: "text",
        required: false,
        placeholder: "Enter Value",
      },
      acceptedDeliveryTerms: {
        label: "Accepted Delivery Terms",
        value: apiData.company.acceptedDeliveryTerms || "",
        editable: editableFields.includes("acceptedDeliveryTerms"),
        type: "select",
        required: false,
        placeholder: "Select Accepted Delivery Terms",
        options: [
          { label: "FOB", value: "fob" },
          { label: "CIF", value: "cif" },
          { label: "EXW", value: "exw" },
          { label: "DDP", value: "ddp" },
          { label: "CFR", value: "cfr" },
        ],
      },
      contractManufacturing: {
        label: "Contract Manufacturing",
        value: apiData.company.contractManufacturing || "",
        editable: editableFields.includes("contractManufacturing"),
        type: "select",
        required: false,
        placeholder: "Select",
        options: [
          { label: "Yes", value: "yes" },
          { label: "No", value: "no" },
          { label: "Both", value: "both" },
        ],
      },
      totalFactorySize: {
        label: "Total Factory Size",
        value: apiData.company.totalFactorySize || "",
        editable: editableFields.includes("totalFactorySize"),
        type: "select",
        required: false,
        placeholder: "Select",
        options: TOTAL_FACTORY_SIZE_OPTIONS.map(({ label, value }) => ({
          label,
          value,
        })),
      },
      infrastructureOverview: {
        label: "Infrastructure Overview",
        value: apiData.company.infrastructureOverview || "",
        editable: editableFields.includes("infrastructureOverview"),
        type: "text",
        required: false,
        placeholder: "Enter Overview",
      },
      productionLinesCount: {
        label: "No. Of production Lines",
        value: apiData.company.productionLinesCount?.toString() ?? "0",
        editable: editableFields.includes("productionLinesCount"),
        type: "number",
        required: false,
      },
      annualOutputValue: {
        label: "Annual Output Value",
        value: apiData.company.annualOutputValue || "",
        editable: editableFields.includes("annualOutputValue"),
        type: "select",
        required: false,
        placeholder: "Select",
        options: [
          { label: "Under $1M", value: "under_1m" },
          { label: "$1M - $10M", value: "1m_10m" },
          { label: "$10M - $50M", value: "10m_50m" },
          { label: "Above $50M", value: "50m_plus" },
        ],
      },
      qcStaffCount: {
        label: "No. of staff",
        value: apiData.company.qcStaffCount?.toString() ?? "0",
        editable: editableFields.includes("qcStaffCount"),
        type: "number",
        required: false,
      },
      rndStaffCount: {
        label: "No. Of R&D Staff",
        value: apiData.company.rndStaffCount?.toString() ?? "0",
        editable: editableFields.includes("rndStaffCount"),
        type: "number",
        required: false,
      },
      productionFacilities: {
        label: "Production Facilities",
        value: apiData.company.productionFacilities || "",
        editable: editableFields.includes("productionFacilities"),
        type: "text",
        required: false,
        placeholder: "Enter Production Facilities",
      },
      warehouseStorageArea: {
        label: "Warehouse Storage Area",
        value: apiData.company.warehouseStorageArea || "",
        editable: editableFields.includes("warehouseStorageArea"),
        type: "text",
        required: false,
        placeholder: "Enter Warehouse Storage Area",
      },
      companyVideoUrl: {
        label: "Company Video",
        value: apiData.company.companyVideoUrl || "",
        editable: editableFields.includes("companyVideoUrl"),
        type: "text",
        required: false,
        placeholder: "Paste Video URL or upload",
      },
      youtubeUrl: {
        label: "YouTube",
        value: apiData.company.youtubeUrl || "",
        editable: editableFields.includes("youtubeUrl"),
        type: "text",
        required: false,
        placeholder: "Enter Link",
      },
      instagramUrl: {
        label: "Instagram",
        value: apiData.company.instagramUrl || "",
        editable: editableFields.includes("instagramUrl"),
        type: "text",
        required: false,
        placeholder: "Enter Link",
      },
      mainProducts: {
        label: "Main Products",
        value: "",
        editable: editableFields.includes("mainProducts"),
        type: "tags",
        required: false,
        placeholder:
          "Enter Main Product. This value will be added only after pressing Enter",
      },
    };
    const ext = apiData.company.businessProfileExtended as
      | Record<string, unknown>
      | undefined;
    if (ext) {
      profileData.companyInfo.companyBrochures = {
        label: "Company Brochures",
        value: (ext.companyBrochures as string[]) || [],
        editable: true,
        type: "uploadMulti",
        required: false,
      };
      profileData.companyInfo.infrastructureImages = {
        label: "Infrastructure Image",
        value: (ext.infrastructureImages as string[]) || [],
        editable: true,
        type: "uploadMulti",
        required: false,
      };
      profileData.companyInfo.warehouseCertificates = {
        label: "Warehouse Certificate",
        value: (ext.warehouseCertificates as string[]) || [],
        editable: true,
        type: "uploadMulti",
        required: false,
      };
    }
    if (ext) {
      profileData.businessProfileExtended = {
        companyBrochures: (ext.companyBrochures as string[]) || [],
        ownBrands:
          (ext.ownBrands as { title?: string; fileUrl?: string }[]) || [],
        otherBrands:
          (ext.otherBrands as { title?: string; fileUrl?: string }[]) || [],
        awardsRecognition:
          (ext.awardsRecognition as {
            title?: string;
            year?: string;
            fileUrl?: string;
          }[]) || [],
        certificatesQuality:
          (ext.certificatesQuality as { title?: string; fileUrl?: string }[]) ||
          [],
        infrastructureImages: (ext.infrastructureImages as string[]) || [],
        warehouseCertificates: (ext.warehouseCertificates as string[]) || [],
        annualProductionCapacity:
          (ext.annualProductionCapacity as {
            productName?: string;
            value?: number;
            unit?: string;
          }[]) || [],
      };
    }
  }

  return profileData;
};

/**
 * Maps ProfileData to API update format
 */
export const mapProfileDataToApiUpdate = (
  profileData: ProfileData,
): Record<string, any> => {
  const updateData: Record<string, any> = {};

  // User info fields (only if editable)
  if (
    profileData.userInfo.firstName?.value !== undefined &&
    profileData.userInfo.firstName.editable
  ) {
    updateData.firstName = profileData.userInfo.firstName.value;
  }
  if (
    profileData.userInfo.lastName?.value !== undefined &&
    profileData.userInfo.lastName.editable
  ) {
    updateData.lastName = profileData.userInfo.lastName.value;
  }
  if (
    profileData.userInfo.phone?.value !== undefined &&
    profileData.userInfo.phone.editable
  ) {
    updateData.phone = profileData.userInfo.phone.value;
  }
  if (
    profileData.userInfo.whatsapp?.value !== undefined &&
    profileData.userInfo.whatsapp.editable
  ) {
    updateData.whatsapp = profileData.userInfo.whatsapp.value;
  }
  if (
    profileData.userInfo.website?.value !== undefined &&
    profileData.userInfo.website.editable &&
    typeof profileData.userInfo.website.value === "string"
  ) {
    updateData.website = profileData.userInfo.website.value;
  }
  if (
    profileData.userInfo.bio?.value !== undefined &&
    profileData.userInfo.bio.editable
  ) {
    updateData.bio = profileData.userInfo.bio.value;
  }
  if (
    profileData.userInfo.profilePic?.value !== undefined &&
    profileData.userInfo.profilePic.editable
  ) {
    updateData.profileImage = profileData.userInfo.profilePic.value;
  }
  if (
    profileData.userInfo.businessType?.value !== undefined &&
    profileData.userInfo.businessType.editable
  ) {
    updateData.businessType = profileData.userInfo.businessType.value;
  }
  if (
    profileData.userInfo.country?.value !== undefined &&
    profileData.userInfo.country.editable
  ) {
    updateData.country = profileData.userInfo.country.value;
  }
  if (
    profileData.userInfo.role?.value !== undefined &&
    profileData.userInfo.role.editable &&
    profileData.userInfo.role.value !== "" &&
    profileData.userInfo.role.value !== null
  ) {
    // Normalize role value to uppercase to match enum format
    const roleValue =
      typeof profileData.userInfo.role.value === "string"
        ? profileData.userInfo.role.value.toUpperCase()
        : profileData.userInfo.role.value;
    updateData.role = roleValue;
  }

  // Social links (only include if value is not empty)
  if (
    profileData.socialLinks.linkedin?.value !== undefined &&
    profileData.socialLinks.linkedin.editable &&
    typeof profileData.socialLinks.linkedin.value === "string" &&
    profileData.socialLinks.linkedin.value.trim() !== ""
  ) {
    updateData.linkedinProfile = profileData.socialLinks.linkedin.value.trim();
  }
  // Legacy socialLinks.website (used by the non-Pepagora ProfilePage) — skip if the
  // Pepagora buyer "Profile" section (userInfo.website) already set this value above.
  if (
    updateData.website === undefined &&
    profileData.socialLinks.website?.value !== undefined &&
    profileData.socialLinks.website.editable &&
    typeof profileData.socialLinks.website.value === "string" &&
    profileData.socialLinks.website.value.trim() !== ""
  ) {
    updateData.website = profileData.socialLinks.website.value.trim();
  }

  // Company fields (only if editable)
  if (
    profileData.companyInfo.companyName?.value !== undefined &&
    profileData.companyInfo.companyName.editable
  ) {
    updateData.companyName = profileData.companyInfo.companyName.value;
  }
  if (
    profileData.companyInfo.legalBusinessName?.value !== undefined &&
    profileData.companyInfo.legalBusinessName.editable &&
    typeof profileData.companyInfo.legalBusinessName.value === "string"
  ) {
    updateData.legalBusinessName =
      profileData.companyInfo.legalBusinessName.value;
    if (!updateData.companyName) {
      updateData.companyName = profileData.companyInfo.legalBusinessName.value;
    }
  }
  if (
    profileData.companyInfo.companyEmail?.value !== undefined &&
    profileData.companyInfo.companyEmail.editable
  ) {
    updateData.companyEmail = profileData.companyInfo.companyEmail.value;
  }
  if (
    profileData.companyInfo.companyPhone?.value !== undefined &&
    profileData.companyInfo.companyPhone.editable
  ) {
    updateData.companyPhone = profileData.companyInfo.companyPhone.value;
  }
  if (
    profileData.companyInfo.address?.value !== undefined &&
    profileData.companyInfo.address.editable
  ) {
    updateData.companyAddress = profileData.companyInfo.address.value;
  }
  if (
    profileData.companyInfo.city?.value !== undefined &&
    profileData.companyInfo.city.editable
  ) {
    updateData.companyCity = profileData.companyInfo.city.value;
  }
  if (
    profileData.companyInfo.state?.value !== undefined &&
    profileData.companyInfo.state.editable
  ) {
    updateData.companyState = profileData.companyInfo.state.value;
  }
  if (
    profileData.companyInfo.country?.value !== undefined &&
    profileData.companyInfo.country.editable
  ) {
    updateData.companyCountry = profileData.companyInfo.country.value;
  }
  // companyCountry is the select-with-options field used in the contact-information UI.
  // If user edits via that field (fieldKey="companyCountry"), persist it here.
  if (
    profileData.companyInfo.companyCountry?.value !== undefined &&
    profileData.companyInfo.companyCountry.editable &&
    typeof profileData.companyInfo.companyCountry.value === "string" &&
    profileData.companyInfo.companyCountry.value.trim() !== ""
  ) {
    updateData.companyCountry =
      profileData.companyInfo.companyCountry.value.trim();
  }
  if (
    profileData.companyInfo.postalCode?.value !== undefined &&
    profileData.companyInfo.postalCode.editable
  ) {
    updateData.companyPostalCode = profileData.companyInfo.postalCode.value;
  }
  if (
    profileData.companyInfo.companyWebsite?.value !== undefined &&
    profileData.companyInfo.companyWebsite.editable &&
    typeof profileData.companyInfo.companyWebsite.value === "string" &&
    profileData.companyInfo.companyWebsite.value.trim() !== ""
  ) {
    updateData.companyWebsite =
      profileData.companyInfo.companyWebsite.value.trim();
  }
  if (
    profileData.companyInfo.description?.value !== undefined &&
    profileData.companyInfo.description.editable
  ) {
    updateData.companyDescription = profileData.companyInfo.description.value;
  }
  if (
    profileData.companyInfo.companyBusinessType?.value !== undefined &&
    profileData.companyInfo.companyBusinessType.editable
  ) {
    updateData.companyBusinessType =
      profileData.companyInfo.companyBusinessType.value;
  }
  if (
    profileData.companyInfo.companySize?.value !== undefined &&
    profileData.companyInfo.companySize.editable
  ) {
    updateData.companySize = profileData.companyInfo.companySize.value;
  }
  if (
    profileData.companyInfo.employeeCount?.value !== undefined &&
    profileData.companyInfo.employeeCount.editable
  ) {
    updateData.employeeCount =
      parseInt(profileData.companyInfo.employeeCount.value as string) ||
      undefined;
  }
  if (
    profileData.companyInfo.industry?.value !== undefined &&
    profileData.companyInfo.industry.editable
  ) {
    updateData.industry = profileData.companyInfo.industry.value;
  }
  if (
    profileData.companyInfo.foundedYear?.value !== undefined &&
    profileData.companyInfo.foundedYear.editable
  ) {
    updateData.foundedYear =
      parseInt(profileData.companyInfo.foundedYear.value as string) ||
      undefined;
  }
  if (
    profileData.companyInfo.gstNumber?.value !== undefined &&
    profileData.companyInfo.gstNumber.editable
  ) {
    updateData.gstNumber = profileData.companyInfo.gstNumber.value;
  }
  if (
    profileData.companyInfo.panNumber?.value !== undefined &&
    profileData.companyInfo.panNumber.editable
  ) {
    updateData.panNumber = profileData.companyInfo.panNumber.value;
  }
  if (
    profileData.companyInfo.taxId?.value !== undefined &&
    profileData.companyInfo.taxId.editable
  ) {
    updateData.taxId = profileData.companyInfo.taxId.value;
  }
  if (
    profileData.companyInfo.registrationNumber?.value !== undefined &&
    profileData.companyInfo.registrationNumber.editable
  ) {
    updateData.registrationNumber =
      profileData.companyInfo.registrationNumber.value;
  }
  if (
    profileData.companyInfo.businessLicense?.value !== undefined &&
    profileData.companyInfo.businessLicense.editable
  ) {
    updateData.businessLicense = profileData.companyInfo.businessLicense.value;
  }
  if (
    profileData.companyInfo.factorySize?.value !== undefined &&
    profileData.companyInfo.factorySize.editable
  ) {
    updateData.factorySize = profileData.companyInfo.factorySize.value;
  }
  if (
    profileData.companyInfo.productionCapacity?.value !== undefined &&
    profileData.companyInfo.productionCapacity.editable
  ) {
    updateData.productionCapacity =
      profileData.companyInfo.productionCapacity.value;
  }
  if (
    profileData.companyInfo.qualityCertifications?.value !== undefined &&
    profileData.companyInfo.qualityCertifications.editable
  ) {
    updateData.qualityCertifications =
      profileData.companyInfo.qualityCertifications.value;
  }
  if (
    profileData.companyInfo.facebookUrl?.value !== undefined &&
    profileData.companyInfo.facebookUrl.editable &&
    typeof profileData.companyInfo.facebookUrl.value === "string" &&
    profileData.companyInfo.facebookUrl.value.trim() !== ""
  ) {
    updateData.facebookUrl = profileData.companyInfo.facebookUrl.value.trim();
  }
  if (
    profileData.companyInfo.linkedinUrl?.value !== undefined &&
    profileData.companyInfo.linkedinUrl.editable &&
    typeof profileData.companyInfo.linkedinUrl.value === "string" &&
    profileData.companyInfo.linkedinUrl.value.trim() !== ""
  ) {
    updateData.linkedinUrl = profileData.companyInfo.linkedinUrl.value.trim();
  }
  if (
    profileData.companyInfo.twitterUrl?.value !== undefined &&
    profileData.companyInfo.twitterUrl.editable &&
    typeof profileData.companyInfo.twitterUrl.value === "string" &&
    profileData.companyInfo.twitterUrl.value.trim() !== ""
  ) {
    updateData.twitterUrl = profileData.companyInfo.twitterUrl.value.trim();
  }
  if (
    profileData.companyInfo.companyLogo?.value !== undefined &&
    profileData.companyInfo.companyLogo.editable
  ) {
    updateData.companyLogo = profileData.companyInfo.companyLogo.value;
  }

  if (
    profileData.companyInfo.selectedMembershipPlanId?.value !== undefined &&
    profileData.companyInfo.selectedMembershipPlanId.editable
  ) {
    const v = profileData.companyInfo.selectedMembershipPlanId.value;
    updateData.selectedMembershipPlanId =
      typeof v === "string" ? v : String(v ?? "");
  }
  if (
    profileData.companyInfo.industryTypes?.value !== undefined &&
    profileData.companyInfo.industryTypes.editable &&
    Array.isArray(profileData.companyInfo.industryTypes.value)
  ) {
    updateData.industryTypes = profileData.companyInfo.industryTypes.value;
    if (profileData.companyInfo.industryTypes.value.length > 0) {
      updateData.industry = profileData.companyInfo.industryTypes.value[0];
    }
  }

  // Seller Business Profile (pepagora-style) fields
  // Note: companyAddress, companyCountry, companyState, companyCity, companyPostalCode are
  // intentionally excluded here — they are already handled by the explicit mapping above
  // (address/state/city/country/postalCode keys). Including them here would overwrite user edits.
  const businessProfileKeys = [
    "legalBusinessName",
    "businessName",
    "legalStatus",
    "documentType",
    "documentNumber",
    "annualTurnover",
    "shippingAddressSameAsBusiness",
    "gstDocumentUrl",
    "panDocumentUrl",
    "iecNumber",
    "iecDocumentUrl",
    "exportPercentage",
    "nearestPort",
    "averageLoadTimeValue",
    "averageLoadTimeUnit",
    "minOrderValueCurrency",
    "minOrderValueAmount",
    "acceptedDeliveryTerms",
    "contractManufacturing",
    "totalFactorySize",
    "infrastructureOverview",
    "productionLinesCount",
    "annualOutputValue",
    "qcStaffCount",
    "rndStaffCount",
    "productionFacilities",
    "warehouseStorageArea",
    "companyVideoUrl",
    "youtubeUrl",
    "instagramUrl",
  ] as const;
  for (const key of businessProfileKeys) {
    const field = profileData.companyInfo[key];
    if (field?.value !== undefined && field.editable) {
      if (field.type === "switch") {
        updateData[key] = !!field.value;
      } else if (field.type === "number") {
        const n = parseInt(String(field.value), 10);
        updateData[key] = isNaN(n) ? undefined : n;
      } else {
        updateData[key] = field.value;
      }
    }
  }
  if (
    profileData.companyInfo.mainMarkets?.value !== undefined &&
    profileData.companyInfo.mainMarkets.editable &&
    Array.isArray(profileData.companyInfo.mainMarkets.value)
  ) {
    updateData.mainMarkets = profileData.companyInfo.mainMarkets.value;
  }
  if (
    profileData.companyInfo.shipmentModes?.value !== undefined &&
    profileData.companyInfo.shipmentModes.editable &&
    Array.isArray(profileData.companyInfo.shipmentModes.value)
  ) {
    updateData.shipmentModes = profileData.companyInfo.shipmentModes.value;
  }
  if (
    profileData.companyInfo.paymentMethods?.value !== undefined &&
    profileData.companyInfo.paymentMethods.editable &&
    Array.isArray(profileData.companyInfo.paymentMethods.value)
  ) {
    updateData.paymentMethods = profileData.companyInfo.paymentMethods.value;
  }
  if (
    profileData.companyInfo.acceptedPaymentCurrency?.value !== undefined &&
    profileData.companyInfo.acceptedPaymentCurrency.editable &&
    Array.isArray(profileData.companyInfo.acceptedPaymentCurrency.value)
  ) {
    updateData.acceptedPaymentCurrency =
      profileData.companyInfo.acceptedPaymentCurrency.value;
  }
  if (
    profileData.companyInfo.paymentTerms?.value !== undefined &&
    profileData.companyInfo.paymentTerms.editable &&
    Array.isArray(profileData.companyInfo.paymentTerms.value)
  ) {
    updateData.paymentTerms = profileData.companyInfo.paymentTerms.value;
  }
  if (
    profileData.companyInfo.languagesSpoken?.value !== undefined &&
    profileData.companyInfo.languagesSpoken.editable &&
    Array.isArray(profileData.companyInfo.languagesSpoken.value)
  ) {
    updateData.languagesSpoken = profileData.companyInfo.languagesSpoken.value;
  }
  if (profileData.businessProfileExtended) {
    updateData.businessProfileExtended = {
      ...profileData.businessProfileExtended,
    };
  }
  if (
    profileData.companyInfo.companyBrochures?.value !== undefined &&
    Array.isArray(profileData.companyInfo.companyBrochures.value)
  ) {
    updateData.businessProfileExtended =
      updateData.businessProfileExtended || {};
    (
      updateData.businessProfileExtended as Record<string, unknown>
    ).companyBrochures = profileData.companyInfo.companyBrochures.value;
  }
  if (
    profileData.companyInfo.infrastructureImages?.value !== undefined &&
    Array.isArray(profileData.companyInfo.infrastructureImages.value)
  ) {
    updateData.businessProfileExtended =
      updateData.businessProfileExtended || {};
    (
      updateData.businessProfileExtended as Record<string, unknown>
    ).infrastructureImages = profileData.companyInfo.infrastructureImages.value;
  }
  if (
    profileData.companyInfo.warehouseCertificates?.value !== undefined &&
    Array.isArray(profileData.companyInfo.warehouseCertificates.value)
  ) {
    updateData.businessProfileExtended =
      updateData.businessProfileExtended || {};
    (
      updateData.businessProfileExtended as Record<string, unknown>
    ).warehouseCertificates =
      profileData.companyInfo.warehouseCertificates.value;
  }

  return updateData;
};
